import { useContext, useState } from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Box, Skeleton, Title } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import type { FilterOption as MultiFilterOption } from 'modules/core/components/MultiFilters/types'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import CycleSelector from 'modules/enps/components/CycleSelector'
import Filters from 'modules/enps/components/Filters'
import useAnalyticsMultiFilters, {
  AnalyticsFilterForm,
} from 'modules/enps/hooks/useAnalyticsMultiFilters'
import { EnpsFormContext } from 'modules/enps/lib/EnpsFormContext'
import type Form from 'modules/enps/store/models/Form'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import filterFields from 'modules/surveys/components/Filters/filterFields'

import BlankState from './BlankState'
import TableResults from './TableResults'
import { useAnalytics } from './useAnalytics'

const ResultsWrapper = () => {
  const { form, pathname } = useOutletContext<{ form: Form; pathname: string }>()
  const { analyticResults, answeredResultsCount, cycle, setCycle, loading } = useAnalytics()
  const { navigation } = useNavigation()
  const navigate = useNavigate()

  const { setResultsFilter } = useContext(EnpsFormContext)
  const {
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    localFilter,
  } = useAnalyticsMultiFilters(setResultsFilter)

  const notEnoughAnswers = answeredResultsCount < 3

  useEffect(() => {
    if (!loading) {
      track('eNPS results view')
    }
  }, [loading])
  const [fields, setFields] = useState<readonly MultiFilterOption[]>([])

  const { loading: loadingFilters } = useAsyncData(async () => {
    const fields = await filterFields()

    setFields(fields)
  }, [])

  const canEdit = form && form.policy.can('editEnpsForm')

  const onNavigateToSettings = () => {
    track(trackingEvents.enps.goToEnpsSettings, {
      coming_from: pathname,
      is_launched: form.isActive,
    })

    navigate(navigation.engagement.enps.configuration.fullPath())
  }

  return (
    <ResponsivePage.Body
      showHeader={false}
      actions={{
        primary:
          form && canEdit
            ? [
                {
                  onClick: () => onNavigateToSettings(),
                  icon: 'settings',
                  variant: 'secondary',
                  label: i18n.t('common.settings'),
                } as const,
              ]
            : [],
      }}
    >
      <Box gap="s16">
        <Title
          illustration="surveys"
          title={i18n.t('enps.results.title_scheduled')}
          description={i18n.t('enps.results.description')}
          variant="600"
        />
        <Box flexDirection="row" gap="s16">
          {loadingFilters && <Skeleton width="s96" height="s32" />}
          {!loadingFilters && (
            <Filters
              label={i18n.t('filters.multi_filters.title')}
              fields={fields}
              filters={filters}
              logicOperator={logicOperator}
              onAddFilter={onAddFilter}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={(isActive, defaultFilter) => {
                onApplyFilters(isActive, defaultFilter)
                setResultsFilter(
                  new AnalyticsFilterForm(filters, logicOperator.values, defaultFilter, isActive)
                )
                const selectedFilter =
                  defaultFilter && defaultFilter.length > 0 ? defaultFilter[0]?.values['field'] : ''
                if (selectedFilter) {
                  track(trackingEvents.enps.applyFiltersEnpsSubmit, {
                    coming_from: pathname,
                    filter_by: selectedFilter,
                    number_results: answeredResultsCount,
                  })
                }
              }}
              onCancel={onCancel}
              filterKey={localFilter}
            />
          )}
          {form.hasSchedule && (
            <CycleSelector analyticResults={analyticResults} cycle={cycle} setCycle={setCycle} />
          )}
        </Box>
        {loading && <Loading />}
        {!loading && notEnoughAnswers && <BlankState />}
        {!loading && !notEnoughAnswers && (
          <Box gap="s16" paddingTop="s16">
            <TableResults />
          </Box>
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default observer(ResultsWrapper)
